/**
 * The deal terms table (VP8.4): the shape the contract carries, and the ORDER the table puts it in.
 *
 * ⛔ Nothing in this file produces a figure anyone reads. Every cell the table prints is a string the
 * contract quotes from an analysis or an email. What IS computed is the order of the rows when a column
 * heading is clicked, and for that a quoted cell's LEADING figure is read as a number: "$3,500,000
 * (reduced from $3,980,000)" sorts as 3,500,000. A cell that does not start with a figure - "See
 * analysis", "n/a (vacant; 5.68% on an imputed rent)" - does not sort as one; it goes to the end, in
 * both directions, because unknown is not the smallest value.
 *
 * The contract types are MIRRORED here, not imported: a frontend file never imports backend source (it
 * typechecks locally and fails the Vercel build - 2026-09-09).
 */

export type FloorKey = 'cap' | 'coc' | 'irr' | 'em' | 'dscr';

/** The order the five floors print in - the order his criteria list them. */
export const FLOOR_ORDER: FloorKey[] = ['cap', 'coc', 'irr', 'em', 'dscr'];

export interface PortalFloorRow {
  /** The analysis's own label for the metric - "5-Yr IRR (Base 7.00% Exit)". */
  label?: string;
  /** PASS, FAIL or N/A (an all-cash deal's DSCR, a land deal's cap). */
  status?: string;
  threshold?: string | null;
  actual?: string | null;
}

export interface PortalScorecard {
  floors?: Partial<Record<FloorKey, PortalFloorRow | null>>;
  /** Counts of the statuses above - checked at import; an N/A leaves the denominator. */
  passed?: number;
  applicable?: number;
}

export interface PortalDealTerms {
  /** Manifest id of the analysis the row quotes. */
  source?: string;
  noiInPlace?: string | null;
  sf?: string | null;
  priceSf?: string | null;
  /** The price the $/SF was printed beside - the import gate refuses one that is not the row's ask. */
  priceSfAt?: string | null;
  scorecard?: PortalScorecard | null;
  /** OUR cap: the going-in cap the analysis prints, with the analysis's own label for it. */
  ourCap?: PortalOurCap | null;
}

export interface PortalOurCap {
  value?: string;
  label?: string;
  /** The price the analysis caps at, where its own wording names one (Harbor Freight: our basis, not the ask). */
  at?: string;
  /** A second cap the analysis prints beside the first, in its own words ("3.77% in-place" beside a pro forma 6.26%). */
  also?: Array<{ value?: string; label?: string }>;
}

/**
 * The OM's cap (VP8.4 round eight): what the seller's offering memorandum prints - one figure, or up to three with
 * the OM's own labels - and the price it prints it at. Quoted and checked against the OM when the contract is built.
 */
export interface PortalOmCap {
  caps?: Array<{ value?: string; label?: string | null }>;
  at?: string | null;
}

export interface TermsGroup {
  label?: string;
  mapColor?: string;
  order?: number;
}

/** What one row reads off a pipeline deal. */
export interface TermsRowDeal {
  id: string;
  name?: string;
  tenant?: string | null;
  city?: string;
  ask?: string | null;
  cap?: string | null;
  verdict?: string | null;
  analysisDate?: string | null;
  status?: { group?: string; label?: string };
  documents?: string[];
  terms?: PortalDealTerms | null;
  omCap?: PortalOmCap | null;
}

export type TermsColumn =
  | 'deal'
  | 'tenant'
  | 'ask'
  | 'omCap'
  | 'ourCap'
  | 'noi'
  | 'sf'
  | 'priceSf'
  | 'analysed'
  | 'read'
  | 'status'
  | 'floors'
  | `floor:${FloorKey}`;

/** The figure a quoted cell STARTS with, as a number - for ordering rows, never for printing. */
export const leadingFigure = (s: string | null | undefined): number | null => {
  if (typeof s !== 'string') return null;
  const m = s.trim().match(/^[±~≈]?\s*(\()?(-)?\$?(\d[\d,]*(?:\.\d+)?)([%x])?(\))?/i);
  if (!m) return null;
  const n = Number(m[3].replace(/,/g, ''));
  if (!Number.isFinite(n)) return null;
  // "(3.22%)" is how the analyses print a negative: accounting brackets, not an aside.
  const negative = m[2] === '-' || (m[1] === '(' && m[5] === ')');
  return negative ? -n : n;
};

type SortValue = number | string | null;

export const sortValue = (
  d: TermsRowDeal,
  col: TermsColumn,
  groupOrder: (group?: string) => number | null
): SortValue => {
  const t = d.terms ?? null;
  switch (col) {
    case 'deal':
      return d.name ?? d.id;
    case 'tenant':
      return d.tenant || null;
    case 'ask':
      return leadingFigure(d.ask);
    case 'omCap':
      return leadingFigure(d.omCap?.caps?.[0]?.value);
    case 'ourCap':
      return leadingFigure(t?.ourCap?.value);
    case 'noi':
      return leadingFigure(t?.noiInPlace);
    case 'sf':
      return leadingFigure(t?.sf);
    case 'priceSf':
      return leadingFigure(t?.priceSf);
    case 'analysed':
      return d.analysisDate || null;
    case 'read':
      return d.verdict || null;
    case 'status':
      return groupOrder(d.status?.group);
    case 'floors': {
      const sc = t?.scorecard;
      if (!sc || typeof sc.passed !== 'number' || typeof sc.applicable !== 'number') return null;
      // More passes first; on a tie the smaller denominator wins - 2 of 4 reads better than 2 of 5.
      return sc.passed - sc.applicable / 100;
    }
    default: {
      const key = col.slice('floor:'.length) as FloorKey;
      return leadingFigure(t?.scorecard?.floors?.[key]?.actual);
    }
  }
};

/** A new array, sorted; rows with no value go LAST in both directions, ties keep the order given. */
export const sortDeals = <T extends TermsRowDeal>(
  deals: readonly T[],
  col: TermsColumn,
  dir: 'asc' | 'desc',
  groupOrder: (group?: string) => number | null
): T[] => {
  const keyed = deals.map((d, i) => ({ d, i, v: sortValue(d, col, groupOrder) }));
  keyed.sort((a, b) => {
    const aBlank = a.v === null || a.v === '';
    const bBlank = b.v === null || b.v === '';
    if (aBlank || bBlank) return aBlank === bBlank ? a.i - b.i : aBlank ? 1 : -1;
    const c =
      typeof a.v === 'number' && typeof b.v === 'number'
        ? a.v - b.v
        : String(a.v).localeCompare(String(b.v), undefined, { numeric: true, sensitivity: 'base' });
    if (c === 0) return a.i - b.i;
    return dir === 'asc' ? c : -c;
  });
  return keyed.map((k) => k.d);
};

/**
 * Whether two quoted prices are the same price - compared as NUMBERS, only to decide whether to repeat the OM's
 * price beside its cap. Nothing is computed for display: "$6,750,000.00" and "$6,750,000" are one price, and a
 * cell that said "at $6,750,000.00" beside an ask of $6,750,000 would read as a second one.
 */
export const samePrice = (a?: string | null, b?: string | null): boolean => {
  const n = (s?: string | null) => {
    const m = (s ?? '').trim().match(/^\$\s?([\d,]+(?:\.\d+)?)/);
    return m ? Number(m[1].replace(/,/g, '')) : null;
  };
  const x = n(a);
  const y = n(b);
  return x !== null && y !== null && x === y;
};

/**
 * A cap cell as its PARTS - each quoted cap with its own label, then the price it is at - so the page can keep every
 * part whole and wrap only between them (the picture showed "6.26% pro / forma"). `capLine` is the same cell as one
 * line of text: what a copy, a card's check or a test reads.
 */
export interface CapParts {
  caps: string[];
  at: string | null;
}
export const capLine = (p: CapParts): string => (p.caps.length ? `${p.caps.join(' / ')}${p.at ? ` at ${p.at}` : ''}` : '');
const quoted = (c: { value?: string; label?: string | null }) => (c.label ? `${c.value} ${c.label}` : (c.value as string));

/** The OM's cap: "2.96% current" and "8.19% pro forma", at "$6,500,000" only when that is not the ask. */
export const omCapParts = (omCap: PortalOmCap | null | undefined, ask?: string | null): CapParts => {
  const caps = (omCap?.caps ?? []).filter((c) => c?.value).map(quoted);
  return { caps, at: caps.length && omCap?.at && !samePrice(omCap.at, ask) ? omCap.at : null };
};
export const omCapText = (omCap: PortalOmCap | null | undefined, ask?: string | null): string => capLine(omCapParts(omCap, ask));

/**
 * Our cap, with the one word that says what kind it is when the analysis's own label says so ("4.65% as-is"), any
 * second cap the analysis prints beside it, and - as with the OM's - the price it is at when that is not the ask.
 */
export const ourCapParts = (c: PortalOurCap | null | undefined, ask?: string | null): CapParts => {
  if (!c?.value) return { caps: [], at: null };
  const l = (c.label ?? '').toLowerCase();
  const kind = /as-is/.test(l) ? 'as-is' : /stabiliz/.test(l) ? 'stabilized' : /pro ?forma/.test(l) ? 'pro forma' : '';
  const caps = [kind ? `${c.value} ${kind}` : c.value, ...(c.also ?? []).filter((a) => a?.value).map(quoted)];
  return { caps, at: c.at && !samePrice(c.at, ask) ? c.at : null };
};
export const ourCapText = (c: PortalOurCap | null | undefined, ask?: string | null): string => capLine(ourCapParts(c, ask));
